/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import cx from 'classnames';
import { Popper, PopperHeader, PopperBody } from '../Popper/Popper';
import Image from '@/components/NextImage';
import { infoIcon } from '../../../constants/images';
import { eventConstants } from '../../../constants/event';
import { Button } from '@/components/Button';
import azCommonStyles from '../../../theme/globals.module.scss';
import type { InfoToolTipProps } from './types';
import styles from './styles.module.scss';
import { Icon, View } from '@az/starc-ui';
import { InfoCircle } from '@az/starc-ui-icons';
import { useLabels } from '@/hooks/useLabels';

const labelMap = {
  label_infoToolTip_button_title: 'label_infoToolTip_button_title',
  label_infoToolTip_button_ariaLabel: 'label_infoToolTip_button_ariaLabel',
};

function TooltipIcon({
  iconSizePx,
  iconSrc,
}: {
  iconSizePx?: number;
  iconSrc?: { alt: string; src: string };
}) {
  if (iconSizePx) {
    return (
      <View
        height={`${iconSizePx}px`}
        width={`${iconSizePx}px`}
        align="center"
        justify="space-around"
      >
        <Icon
          svg={InfoCircle}
          size={(iconSizePx * 0.67) / 4}
          attributes={{ style: { color: '#6E6E6E' } }}
        />
      </View>
    );
  }
  return (
    <Image
      className={styles.tooltipImage}
      src={iconSrc ? iconSrc.src : infoIcon.src}
      alt={iconSrc ? iconSrc.alt : infoIcon.alt}
      width={24}
      height={24}
    />
  );
}

const InfoToolTip = ({
  header,
  body,
  bodyComponent,
  isCorePricePadding = false,
  placement = 'bottom',
  strategy = 'fixed',
  classes = {},
  hideCloseIcon = false,
  isCustomElement = false,
  variant = 'ghost',
  customElement,
  'data-testid': dataTestId,
  setInfoToolTipIsOpen,
  iconSrc,
  divStyle,
  iconSizePx,
  headerTag = 'h1',
}: InfoToolTipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const infoIconRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId();
  const tooltipId = `tooltip-${dataTestId || 'info'}-${uniqueId}`;
  const { label_infoToolTip_button_title, label_infoToolTip_button_ariaLabel } =
    useLabels(labelMap);

  const openTooltip = () => {
    setIsOpen(true);
    if (setInfoToolTipIsOpen !== undefined) {
      setInfoToolTipIsOpen(true);
    }
  };

  const closeTooltip = useCallback(() => {
    setIsOpen(false);
    if (setInfoToolTipIsOpen !== undefined) {
      setInfoToolTipIsOpen(false);
    }
  }, [setInfoToolTipIsOpen]);

  const handleOnMouseClick = () => {
    return isOpen ? closeTooltip() : openTooltip();
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (infoIconRef.current && !infoIconRef.current.contains(event.target as Node)) {
        closeTooltip();
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e) {
        if (e.keyCode === eventConstants.enterKeyCode || e.keyCode === eventConstants.escKeyCode) {
          closeTooltip();
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeTooltip]);

  return (
    <>
      <div
        ref={infoIconRef}
        className={cx(
          divStyle ? '' : styles.toolTipContainer,
          iconSizePx ? styles.hasIconSizePxOverride : '',
          classes.container,
        )}
        style={divStyle}
      >
        <Button
          data-testid={dataTestId}
          aria-describedby={isOpen ? tooltipId : undefined}
          aria-expanded={isOpen}
          aria-label={label_infoToolTip_button_ariaLabel}
          aria-haspopup="true"
          title={label_infoToolTip_button_title}
          onFocus={openTooltip}
          onBlur={closeTooltip}
          onMouseDown={handleOnMouseClick}
          type="button"
          customClass={
            iconSizePx ? styles.hasIconSizePxOverride : cx(styles.toolTipIcon, classes.icon)
          }
          variant={variant}
        >
          {isCustomElement ? (
            customElement
          ) : (
            <TooltipIcon iconSizePx={iconSizePx} iconSrc={iconSrc} />
          )}
        </Button>
        <Popper
          isOpen={isOpen}
          anchorEl={infoIconRef.current}
          placement={placement}
          strategy={strategy}
          classes={{
            popper: cx(styles.toolTipPopperContainer, styles.popperPadding, {
              [classes.popper || '']: classes.popper,
            }),
            paper: styles.toolTipPopperPaper,
          }}
          elevation={4}
          isCorePricePadding={isCorePricePadding}
          popperId={tooltipId}
          popperRole="tooltip"
        >
          <PopperHeader
            classes={{
              title: classes.title || styles.toolTipPopperTitle,
              heading: styles.toolTipPopperHeading,
            }}
            handleClose={closeTooltip}
            hideCloseIcon={hideCloseIcon}
          >
            {React.createElement(
              headerTag,
              { className: azCommonStyles['az-button-text'] },
              <strong>{header}</strong>,
            )}
          </PopperHeader>
          {bodyComponent ? (
            <PopperBody
              classes={{
                body: classes.body,
              }}
            >
              {bodyComponent as React.ReactNode}
            </PopperBody>
          ) : (
            <PopperBody>
              <div
                className={azCommonStyles['az-body-2-regular']}
                dangerouslySetInnerHTML={(() => ({ __html: body }))()}
              />
            </PopperBody>
          )}
        </Popper>
      </div>
    </>
  );
};

export default InfoToolTip;
